import { useEffect, useState } from 'react';

import { useTheme } from '@emotion/react';
import { colors } from '@revenuemarket/design';
import { ExpandLessIcon, ExpandMoreIcon } from '@revenuemarket/ui/Icon';
import { Typography } from 'antd';
import { useRouter } from 'next/router';

import { MenuItem } from '~constants/menu-items';
import { mixpanel } from '~pages/_app';
import { taxInvoice } from '~utils/common/getTextWithFeatureFlag';

import * as S from './style';

interface MenuListProps {
  onClick?: (menuInfo?: MenuItem) => void;
  menuItems?: MenuItem[];
}

interface OutOfFeature {
  outOfFeature?: { taxInvoice: boolean };
}

export interface MenuItemProps extends MenuItem, OutOfFeature, Pick<MenuListProps, 'onClick'> {
  active?: boolean;
}

export default function MenuList({
  onClick,
  menuItems = [],
  outOfFeature,
}: MenuListProps & OutOfFeature) {
  const router = useRouter();

  return (
    <>
      {menuItems.map((item) => {
        return (
          <div key={item.id}>
            <MenuItemComponent
              targetKey={item.targetKey}
              key={`${item.id}-${item.name}`}
              id={item.id}
              name={item.name}
              link={item.link}
              icon={item.icon}
              subMenu={item.subMenu}
              active={item.link === router.asPath}
              event={item.event}
              divide={item.divide}
              onClick={onClick}
              outOfFeature={outOfFeature}
            />
          </div>
        );
      })}
    </>
  );
}

function MenuItemComponent({
  targetKey,
  id,
  icon,
  name,
  link,
  active,
  subMenu,
  onClick,
  outOfFeature,
  event,
  divide,
}: MenuItemProps) {
  const theme = useTheme();
  const router = useRouter();
  const [isExpend, setIsExpend] = useState<boolean>();

  useEffect(() => {
    const defaultIsExpand = subMenu?.some((item) => item.link === router.asPath);
    setIsExpend(defaultIsExpand);
  }, [subMenu, router.asPath]);

  const handleClick = () => {
    if (link) {
      router.push(link);
    }

    if (subMenu) {
      setIsExpend((prevState) => !prevState);
    }

    if (onClick) {
      onClick({ id, name, link });
    }

    if (event) {
      mixpanel.track(event);
    }
  };

  return (
    <>
      <S.Anchor onClick={handleClick} active={active} id={targetKey}>
        <Typography.Text
          style={{
            color: active ? colors.grayscale700 : theme.colors.grayscale400,
            fontWeight: active ? 600 : 'normal',
            fontSize: 14,
          }}
        >
          {name}
        </Typography.Text>
        {subMenu && (
          <S.ExpandIconWrapper>
            {isExpend ? (
              <ExpandLessIcon fill={theme.colors.grayscale400} />
            ) : (
              <ExpandMoreIcon fill={theme.colors.grayscale400} />
            )}
          </S.ExpandIconWrapper>
        )}
      </S.Anchor>
      {subMenu && (
        <S.SubMenuWrapper isExpand={isExpend}>
          {subMenu.map((item) => {
            const menuName = item?.name === '간편 매출 연결' ? taxInvoice.title : item?.name;
            return (
              <div key={item.id}>
                <MenuItemComponent
                  key={`${item.id}-${item.name}`}
                  id={item.id}
                  name={menuName}
                  link={item.link}
                  icon={item.icon}
                  subMenu={item.subMenu}
                  active={item.link === router.asPath}
                  onClick={onClick}
                />
              </div>
            );
          })}
        </S.SubMenuWrapper>
      )}
      {divide && (
        <div
          style={{
            height: '1px',
            background: 'rgba(255, 255, 255, 0.1)',
            marginTop: 8,
            marginBottom: 8,
          }}
        />
      )}
    </>
  );
}
